import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiResponse } from '@/lib/api';
import { AdminAPI } from '@shared/types/api';

// Feedback Triage hooks (docs/TRIAGE.md §7). Same conventions as useAdminOps:
// infinite queries with `before` cursors, 10s polling on live surfaces, a
// shared invalidate helper for mutations.

const TRIAGE_REFETCH_INTERVAL_MS = 10_000;

// Playbook slugs may contain slashes (e.g. "answers/general"); the server
// route is /playbook/:slug(*) so a raw slash in the path is fine, but every
// segment still needs escaping for anything else.
function encodeSlug(slug: string): string {
  return slug.split('/').map(encodeURIComponent).join('/');
}

export interface TriageTicketFilters {
  /** Omit for the open set; pass 'resolved' | 'spam' | 'new' | ... for exact-status views. */
  status?: string;
  disposition?: AdminAPI.TriageDisposition;
  category?: string;
}

export function useTriageTickets(filters: TriageTicketFilters = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['admin-triage-tickets', filters],
    enabled,
    refetchInterval: TRIAGE_REFETCH_INTERVAL_MS,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<AdminAPI.GetTriageTicketsResponse> => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.disposition) params.set('disposition', filters.disposition);
      if (filters.category) params.set('category', filters.category);
      if (pageParam) params.set('before', pageParam);
      const qs = params.toString();
      return api.get(`/admin/triage/tickets${qs ? `?${qs}` : ''}`);
    },
    getNextPageParam: (lastPage) => lastPage.data?.nextBefore ?? undefined,
  });
}

export function useTriageTicketDetail(ticketId: string | null) {
  return useQuery({
    queryKey: ['admin-triage-ticket', ticketId],
    enabled: !!ticketId,
    refetchInterval: TRIAGE_REFETCH_INTERVAL_MS,
    queryFn: async (): Promise<AdminAPI.GetTriageTicketDetailResponse> =>
      api.get(`/admin/triage/tickets/${ticketId}`),
  });
}

/** Filter-tab + nav-badge counts. Polls every 60s (same cadence as the ops badge). */
export function useTriageCounts(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['admin-triage-counts'],
    enabled: opts.enabled ?? true,
    refetchInterval: 60_000,
    queryFn: async (): Promise<AdminAPI.GetTriageCountsResponse['data']> => {
      const res: AdminAPI.GetTriageCountsResponse = await api.get('/admin/triage/counts');
      return res.data;
    },
  });
}

export function useTriageUserSearch(q: string) {
  const query = q.trim();
  return useQuery({
    queryKey: ['admin-triage-user-search', query],
    enabled: query.length >= 2,
    queryFn: async (): Promise<AdminAPI.TriageUserSearchResponse> =>
      api.get(`/admin/triage/user-search?q=${encodeURIComponent(query)}`),
  });
}

export function useTriageHistory(params: { userId?: string; email?: string }, enabled = true) {
  return useQuery({
    queryKey: ['admin-triage-history', params.userId ?? null, params.email ?? null],
    enabled: enabled && (!!params.userId || !!params.email),
    queryFn: async (): Promise<AdminAPI.GetTriageHistoryResponse> => {
      const search = new URLSearchParams();
      if (params.userId) search.set('userId', params.userId);
      else if (params.email) search.set('email', params.email);
      return api.get(`/admin/triage/history?${search.toString()}`);
    },
  });
}

function useInvalidateTriage() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['admin-triage-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['admin-triage-ticket'] });
    queryClient.invalidateQueries({ queryKey: ['admin-triage-counts'] });
    queryClient.invalidateQueries({ queryKey: ['admin-triage-history'] });
    queryClient.invalidateQueries({ queryKey: ['admin-triage-playbook'] });
  };
}

// Client-side mirrors of the server's list filter and counts query
// (services/triage/tickets.ts), used only to patch the cache optimistically.
const OPEN_STATUSES: readonly string[] = ['new', 'classified', 'awaiting_approval'];

type TriageTicketsCache = { pages: AdminAPI.GetTriageTicketsResponse[]; pageParams: unknown[] };
type TriageCounts = NonNullable<AdminAPI.GetTriageCountsResponse['data']>;
type TriageCountKey = Exclude<keyof TriageCounts, 'pendingPlaybookProposals'>;

function ticketMatchesFilters(ticket: AdminAPI.TriageTicketSummary, filters: TriageTicketFilters): boolean {
  const statusOk = filters.status
    ? ticket.status === filters.status
    : OPEN_STATUSES.includes(ticket.status) || ticket.status === 'waiting_on_user';
  return (
    statusOk &&
    (!filters.disposition || ticket.disposition === filters.disposition) &&
    (!filters.category || ticket.category === filters.category)
  );
}

function countKeysFor(ticket: AdminAPI.TriageTicketSummary): TriageCountKey[] {
  const keys: TriageCountKey[] = [];
  if (OPEN_STATUSES.includes(ticket.status)) {
    keys.push('open');
    if (ticket.disposition === 'needs_review') keys.push('needsReview');
    if (ticket.disposition === 'needs_action') keys.push('needsAction');
    if (ticket.disposition === 'auto_answerable') keys.push('autoAnswerable');
  }
  if (ticket.status === 'new') keys.push('unclassified');
  if (ticket.status === 'awaiting_approval') keys.push('awaitingApproval');
  if (ticket.status === 'waiting_on_user') keys.push('waitingOnUser');
  return keys;
}

const SET_STATUS_MUTATION_KEY = ['admin-triage-set-status'];

// Eager: the list rows, detail pane, and tab counts are patched before the
// request goes out, so marking tickets done in quick succession feels instant.
// The server truth is refetched once the last in-flight status change settles
// (refetching earlier would flash not-yet-processed tickets back into the list).
export function useSetTriageStatus() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationKey: SET_STATUS_MUTATION_KEY,
    mutationFn: async (params: {
      ticketId: string;
      status: AdminAPI.SetTriageStatusRequest['status'];
    }): Promise<ApiResponse<unknown>> =>
      api.post(`/admin/triage/tickets/${params.ticketId}/status`, { status: params.status }),
    onMutate: async ({ ticketId, status }) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ['admin-triage-tickets'] }),
        queryClient.cancelQueries({ queryKey: ['admin-triage-ticket', ticketId] }),
        queryClient.cancelQueries({ queryKey: ['admin-triage-counts'] }),
      ]);

      let previous: AdminAPI.TriageTicketSummary | undefined;

      queryClient.setQueryData<AdminAPI.GetTriageTicketDetailResponse>(
        ['admin-triage-ticket', ticketId],
        (old) => {
          if (!old?.data) return old;
          previous = old.data.ticket;
          return { ...old, data: { ...old.data, ticket: { ...old.data.ticket, status } } };
        }
      );

      for (const query of queryClient.getQueryCache().findAll({ queryKey: ['admin-triage-tickets'] })) {
        const filters = (query.queryKey[1] ?? {}) as TriageTicketFilters;
        queryClient.setQueryData<TriageTicketsCache>(query.queryKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => {
              if (!page.data) return page;
              const tickets = page.data.tickets.flatMap((t) => {
                if (t.id !== ticketId) return [t];
                previous ??= t;
                const next = { ...t, status };
                return ticketMatchesFilters(next, filters) ? [next] : [];
              });
              return { ...page, data: { ...page.data, tickets } };
            }),
          };
        });
      }

      if (previous) {
        const before = previous;
        queryClient.setQueryData<TriageCounts>(['admin-triage-counts'], (old) => {
          if (!old) return old;
          const next = { ...old };
          for (const key of countKeysFor(before)) next[key] = Math.max(0, next[key] - 1);
          for (const key of countKeysFor({ ...before, status })) next[key] += 1;
          return next;
        });
      }
    },
    onSettled: (_data, error) => {
      // This mutation still counts as in flight while onSettled runs.
      const inFlight = queryClient.isMutating({ mutationKey: SET_STATUS_MUTATION_KEY });
      if (error || inFlight <= 1) invalidate();
    },
  });
}

export function useLinkTriageUser() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (params: { ticketId: string; userId: string | null }): Promise<ApiResponse<unknown>> =>
      api.post(`/admin/triage/tickets/${params.ticketId}/link-user`, { userId: params.userId }),
    onSuccess: invalidate,
  });
}

export function useClassifyTriageTicket() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (
      params: { ticketId: string } & AdminAPI.UpdateTriageTicketRequest
    ): Promise<ApiResponse<unknown>> => {
      const { ticketId, ...body } = params;
      return api.post(`/admin/triage/tickets/${ticketId}/classify`, body);
    },
    onSuccess: invalidate,
  });
}

/** Merge other tickets INTO the target ticket (target survives, sources close as merged). */
export function useMergeTriageTickets() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (params: {
      targetTicketId: string;
      sourceTicketIds: string[];
    }): Promise<AdminAPI.MergeTriageTicketsResponse> =>
      api.post(`/admin/triage/tickets/${params.targetTicketId}/merge`, {
        sourceTicketIds: params.sourceTicketIds,
      }),
    onSuccess: invalidate,
  });
}

/** Detach one thread from its ticket onto a fresh ticket. */
export function useUnmergeTriageThread() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (threadId: string): Promise<AdminAPI.UnmergeTriageThreadResponse> =>
      api.post(`/admin/triage/threads/${threadId}/unmerge`, {}),
    onSuccess: invalidate,
  });
}

/** Enqueue a fresh triage.classify run for the ticket. */
export function useReclassifyTriageTicket() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (ticketId: string): Promise<ApiResponse<{ runId: string; outcome: string }>> =>
      api.post(`/admin/triage/tickets/${ticketId}/reclassify`, {}),
    onSuccess: invalidate,
  });
}

/** "Remember this" teach box: files a pending playbook proposal for review. */
export function useTriageRemember() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (params: { ticketId: string; text: string }): Promise<ApiResponse<unknown>> =>
      api.post(`/admin/triage/tickets/${params.ticketId}/remember`, { text: params.text }),
    onSuccess: invalidate,
  });
}

export function useEditTriageDraft() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (
      params: { draftId: string } & AdminAPI.EditTriageDraftRequest
    ): Promise<ApiResponse<unknown>> => {
      const { draftId, ...body } = params;
      return api.put(`/admin/triage/drafts/${draftId}`, body);
    },
    onSuccess: invalidate,
  });
}

/** The only send path. Pass `body` to edit-and-send in one call. */
export function useSendTriageDraft() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (
      params: { draftId: string } & AdminAPI.SendTriageDraftRequest
    ): Promise<AdminAPI.SendTriageDraftResponse> => {
      const { draftId, ...body } = params;
      return api.post(`/admin/triage/drafts/${draftId}/send`, body);
    },
    // Also on failure: actions that ran before a failed step are recorded on
    // the draft and must show as done.
    onSettled: invalidate,
  });
}

// A reply the admin wrote themselves; works with or without a suggestion.
export function useSendTriageReply() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (
      params: { ticketId: string } & AdminAPI.SendTriageReplyRequest
    ): Promise<AdminAPI.SendTriageDraftResponse> => {
      const { ticketId, ...body } = params;
      return api.post(`/admin/triage/tickets/${ticketId}/reply`, body);
    },
    onSettled: invalidate,
  });
}

// "Ask AI": the instruction becomes one more resolution option on the ticket.
export function useTriageAssist() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (
      params: { ticketId: string } & AdminAPI.TriageAssistRequest
    ): Promise<AdminAPI.TriageAssistResponse> => {
      const { ticketId, ...body } = params;
      return api.post(`/admin/triage/tickets/${ticketId}/assist`, body);
    },
    onSuccess: invalidate,
  });
}

export function useRejectTriageDraft() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (params: { draftId: string; note?: string }): Promise<ApiResponse<unknown>> => {
      const { draftId, ...body } = params;
      return api.post(`/admin/triage/drafts/${draftId}/reject`, body);
    },
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Playbook
// ---------------------------------------------------------------------------

export function useTriagePlaybook(enabled = true) {
  return useQuery({
    queryKey: ['admin-triage-playbook'],
    enabled,
    refetchInterval: 60_000,
    queryFn: async (): Promise<AdminAPI.GetTriagePlaybookResponse> => api.get('/admin/triage/playbook'),
  });
}

export function useTriagePlaybookRevisions(slug: string | null) {
  return useQuery({
    queryKey: ['admin-triage-playbook-revisions', slug],
    enabled: !!slug,
    queryFn: async (): Promise<AdminAPI.GetTriagePlaybookRevisionsResponse> =>
      api.get(`/admin/triage/playbook/${encodeSlug(slug!)}/revisions`),
  });
}

export function useUpdateTriagePlaybookDoc() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: { slug: string } & AdminAPI.UpdateTriagePlaybookDocRequest
    ): Promise<ApiResponse<unknown>> => {
      const { slug, ...body } = params;
      return api.put(`/admin/triage/playbook/${encodeSlug(slug)}`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-triage-playbook'] });
      queryClient.invalidateQueries({ queryKey: ['admin-triage-playbook-revisions'] });
    },
  });
}

export function useReviewTriageProposal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: { proposalId: string } & AdminAPI.ReviewTriageProposalRequest
    ): Promise<ApiResponse<unknown>> => {
      const { proposalId, ...body } = params;
      return api.post(`/admin/triage/proposals/${proposalId}/review`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-triage-playbook'] });
      queryClient.invalidateQueries({ queryKey: ['admin-triage-playbook-revisions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-triage-counts'] });
    },
  });
}

/** Manual Gmail sync trigger ("Sync now" in the inbox header). */
export function useTriageSync() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (): Promise<AdminAPI.TriageSyncResponse> => api.post('/admin/triage/sync', {}),
    onSuccess: invalidate,
  });
}

// "Mark all done": resolve every ticket matching the given inbox filters in one
// sweep (no filters = the open set). Closed statuses are rejected server-side.
export function useResolveAllTriage() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (
      filters: AdminAPI.ResolveAllTriageTicketsRequest = {}
    ): Promise<AdminAPI.ResolveAllTriageTicketsResponse> =>
      api.post('/admin/triage/tickets/resolve-all', filters),
    onSuccess: invalidate,
  });
}

// Global (ticket-less) triage runs: `triage.reflect` is the learning loop,
// `triage.bootstrap` the one-off playbook seeding from sent mail.
export function useEnqueueTriageGlobalRun() {
  const invalidate = useInvalidateTriage();
  return useMutation({
    mutationFn: async (
      body: AdminAPI.EnqueueTriageGlobalRunRequest
    ): Promise<AdminAPI.EnqueueTriageGlobalRunResponse> => api.post('/admin/triage/runs', body),
    onSuccess: invalidate,
  });
}
